// Dashboard utility functions

export const formatCurrency = (amount: number, currency = 'ILS'): string => {
  return new Intl.NumberFormat('en-IL', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'k';
  }
  return num.toString();
};

export const formatPercentage = (value: number, precision = 1): string => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(precision)}%`;
};

export const calculateGrowthPercentage = (current: number, previous: number): number => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
};

export const getTimeRangeFromString = (range: string): number => {
  const timeRangeMap: Record<string, number> = {
    'Today': 1,
    'Yesterday': 1,
    'This Week': 7,
    'This Month': 30,
    'Last 3 Months': 90,
    'This Year': 365,
  };
  
  return timeRangeMap[range] || 30;
};

export const getStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    // Order statuses
    completed: '#4caf50',
    delivered: '#4caf50',
    processing: '#2196f3',
    created: '#ff9800',
    pending: '#ff9800',
    draft: '#9e9e9e',
    cancelled: '#f44336',
    failed: '#d32f2f',
    
    // Payment statuses
    paid: '#4caf50',
    refunded: '#ff9800',
    
    // Shipment statuses
    ready: '#2196f3',
    in_transit: '#9c27b0',
    shipped: '#9c27b0',
  };
  
  return statusColors[status.toLowerCase()] || '#9e9e9e';
};

export const getStatusChip = (status: string) => {
  const color = getStatusColor(status);
  return {
    color,
    backgroundColor: `${color}20`,
    label: status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' '),
  };
};

export const formatDate = (dateString: string, options?: Intl.DateTimeFormatOptions): string => {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  
  return new Date(dateString).toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  const intervals = [
    { label: 'year', seconds: 31536000 },
    { label: 'month', seconds: 2592000 },
    { label: 'day', seconds: 86400 },
    { label: 'hour', seconds: 3600 },
    { label: 'minute', seconds: 60 },
  ];
  
  for (const interval of intervals) {
    const count = Math.floor(diffInSeconds / interval.seconds);
    if (count >= 1) {
      return `${count} ${interval.label}${count > 1 ? 's' : ''} ago`;
    }
  }
  
  return 'Just now';
};

export const calculateAverageOrderValue = (totalRevenue: number, totalOrders: number): number => {
  return totalOrders > 0 ? totalRevenue / totalOrders : 0;
};

export const calculateConversionRate = (conversions: number, total: number): number => {
  return total > 0 ? (conversions / total) * 100 : 0;
};

export const sortTableData = <T>(
  data: T[],
  sortKey: keyof T,
  sortDirection: 'asc' | 'desc'
): T[] => {
  return [...data].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
};

export const generateChartColor = (index: number, total: number, baseColor = '#2196f3'): string => {
  const hue = (index * 360) / total;
  return `hsl(${hue}, 70%, 50%)`;
};

export const getActivityIcon = (activityType: string): string => {
  const iconMap: Record<string, string> = {
    user_registration: '👤',
    new_order: '📦',
    payment_received: '💳',
    order_shipped: '🚚',
    order_delivered: '✅',
    order_cancelled: '❌',
    wallet_deposit: '💰',
    wallet_withdrawal: '💸',
  };
  
  return iconMap[activityType] || '📊';
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}; 